import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { fetchAvailableEmployeeSlotsForDay } from './calendarMonthAvailability';
import {
  checkCalendarDropAvailabilityForItem,
  rescheduleCalendarItem,
} from './rescheduleCalendarItem';
import { resolveAppointmentEmployeeId } from '../utils/normalizeCompanyAppointment';
import '../styles/TeamLabelCalendar.css';

export default function CalendarMonthRescheduleDialog({
  open,
  appointment,
  targetDay,
  companyId,
  companyData,
  companyHours,
  user,
  onClose,
  onComplete,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const employeeId = useMemo(
    () => (appointment ? resolveAppointmentEmployeeId(appointment) : null),
    [appointment]
  );

  const title = appointment?.clientName || appointment?.serviceName || 'Appointment';

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, saving]);

  useEffect(() => {
    if (!open || !appointment || !targetDay || !employeeId || !companyId) {
      setSlots([]);
      setSelectedSlot(null);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setSelectedSlot(null);

    fetchAvailableEmployeeSlotsForDay({
      companyId,
      companyData,
      companyHours,
      targetDay,
      employeeId,
      appointment,
    })
      .then(({ slots: nextSlots, error: nextError }) => {
        if (cancelled) return;
        setSlots(nextSlots);
        setError(nextError || '');
      })
      .catch((err) => {
        if (cancelled) return;
        setSlots([]);
        setError(err.message || 'Unable to load available times.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, appointment, targetDay, employeeId, companyId, companyData, companyHours]);

  const handleConfirm = useCallback(async () => {
    if (!selectedSlot || !appointment || !employeeId || !companyId) return;

    setSaving(true);
    setError('');

    try {
      const availability = await checkCalendarDropAvailabilityForItem({
        companyId,
        companyData,
        appointment,
        targetEmployeeId: employeeId,
        newStartTime: selectedSlot,
      });

      if (!availability.isAvailable) {
        setError(availability.reason || 'That time is no longer available.');
        setSaving(false);
        return;
      }

      await rescheduleCalendarItem({
        companyId,
        appointment,
        newStartTime: selectedSlot,
        targetEmployeeId: employeeId,
        targetEmployeeName: appointment.employeeName || appointment.employee?.name || '',
        user,
      });

      onComplete?.({ ok: true, newStartTime: selectedSlot });
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to reschedule appointment.');
      onComplete?.({ ok: false, reason: err.message || 'Failed to reschedule appointment.' });
    } finally {
      setSaving(false);
    }
  }, [
    appointment,
    companyData,
    companyId,
    employeeId,
    onClose,
    onComplete,
    selectedSlot,
    user,
  ]);

  if (!open) return null;

  return (
    <div className="team-cal-v3-dialog-shell" role="presentation">
      <button
        type="button"
        className="team-cal-v3-dialog-shell__backdrop"
        aria-label="Close reschedule dialog"
        onClick={saving ? undefined : onClose}
        disabled={saving}
      />
      <div
        className="team-cal-v3-dialog-shell__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-cal-v3-reschedule-title"
      >
        <header className="team-cal-v3-dialog__header">
          <div className="team-cal-v3-dialog__heading">
            <h2 id="team-cal-v3-reschedule-title" className="team-cal-v3-dialog__title">
              Choose a time
            </h2>
            <p className="team-cal-v3-dialog__subtitle">
              Move <strong>{title}</strong> to{' '}
              <strong>{targetDay ? format(targetDay, 'EEEE, MMMM d, yyyy') : 'selected day'}</strong>
            </p>
          </div>
          <button
            type="button"
            className="team-cal-v3-dialog__close"
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
          >
            ×
          </button>
        </header>

        <div className="team-cal-v3-dialog__body">
          {loading ? (
            <div className="team-cal-v3-dialog__loading" role="status" aria-live="polite">
              <div className="team-cal-v3-spinner" aria-hidden />
              <span>Loading available times…</span>
            </div>
          ) : null}

          {!loading && error ? (
            <p className="team-cal-v3-dialog__error" role="alert">
              {error}
            </p>
          ) : null}

          {!loading && !error && slots.length === 0 ? (
            <p className="team-cal-v3-muted team-cal-v3-dialog__empty">
              No available times for this staff member on that day.
            </p>
          ) : null}

          {!loading && slots.length > 0 ? (
            <div className="team-cal-v3-dialog__slots" role="listbox" aria-label="Available times">
              {slots.map((slot) => {
                const selected = selectedSlot?.getTime?.() === slot.getTime?.();
                return (
                  <button
                    key={slot.toISOString()}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`team-cal-v3-dialog__slot${selected ? ' team-cal-v3-dialog__slot--selected' : ''}`}
                    onClick={() => setSelectedSlot(slot)}
                    disabled={saving}
                  >
                    {format(slot, 'h:mm a')}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <footer className="team-cal-v3-dialog__footer">
          <button
            type="button"
            className="team-cal-v3-btn team-cal-v3-dialog__btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="team-cal-v3-btn team-cal-v3-btn--primary team-cal-v3-dialog__btn"
            onClick={handleConfirm}
            disabled={saving || !selectedSlot}
          >
            {saving ? 'Saving…' : 'Reschedule'}
          </button>
        </footer>
      </div>
    </div>
  );
}
